import * as React from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import ApiLink from 'API';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    LinearProgress,
    Snackbar,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

// Bulk-import IMEIs from Excel (column `imei`) or a free-form textarea.
//
// IMPORTANT ordering: a successful Submit auto-closes the Dialog, and the
// imported items are only committed to the parent's ImeiProduct state in
// Dialog `onExited` (i.e. after the close transition finishes + portal is
// gone). Updating parent state while the Dialog is mid-close leaves the
// surrounding ProductOrder cards in a stale layout — see
// memory/mui-nested-dialog-stale-dom.md.
export default function BulkImeiImport({ RequestProduct, ImeiProduct, setImeiProduct, onClosed, onErrors }) {
    const [open, setOpen] = React.useState(false);
    const [text, setText] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [progress, setProgress] = React.useState({ done: 0, total: 0 });
    const [result, setResult] = React.useState(null);
    const fileInputRef = React.useRef(null);

    // Pending commit — set after a successful Submit, applied in onExited.
    const pendingRef = React.useRef(null);
    // Toast shown AFTER the dialog has fully closed (so user gets immediate
    // confirmation even though everything happens in the background).
    const [toast, setToast] = React.useState(null);

    const allowedProductIds = React.useMemo(
        () => new Set((RequestProduct || []).map((r) => String(r.productId))),
        [RequestProduct],
    );

    const reset = () => {
        setText('');
        setBusy(false);
        setProgress({ done: 0, total: 0 });
        setResult(null);
    };

    const handleClose = () => {
        if (busy) return;
        setOpen(false);
    };

    // Fires when MUI's close transition is fully complete and the portal is
    // gone — safe point to mutate parent state.
    const handleExited = () => {
        const pending = pendingRef.current;
        pendingRef.current = null;
        if (pending && pending.newItems && pending.newItems.length > 0) {
            setImeiProduct((prev) => [...(prev || []), ...pending.newItems]);
            setToast({
                severity: pending.errors.length === 0 ? 'success' : 'warning',
                text: `Added ${pending.newItems.length}/${pending.requested} IMEI(s)${
                    pending.errors.length > 0 ? ` • ${pending.errors.length} error(s) — see below` : ''
                }`,
            });
            // Hand the errors off to the parent so they can be shown in a
            // persistent panel outside the (now-closed) dialog.
            if (typeof onErrors === 'function') {
                onErrors(pending.errors || []);
            }
        }
        reset();
        if (typeof onClosed === 'function') onClosed();
    };

    const parseImeiLines = (raw) =>
        Array.from(
            new Set(
                String(raw || '')
                    .split(/[\s,;\r\n]+/)
                    .map((s) => s.trim())
                    .filter(Boolean),
            ),
        );

    const handleExcelPick = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            try {
                const data = new Uint8Array(ev.target.result);
                const wb = XLSX.read(data, { type: 'array' });
                const sheet = wb.Sheets[wb.SheetNames[0]];
                const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
                const collected = [];
                rows.forEach((row) => {
                    Object.keys(row).forEach((k) => {
                        if (String(k).trim().toLowerCase() === 'imei') {
                            const v = String(row[k] || '').trim();
                            if (v) collected.push(v);
                        }
                    });
                });
                if (collected.length === 0) {
                    setResult({ severity: 'error', text: 'No "imei" column found in the Excel file.' });
                    return;
                }
                setText((prev) => {
                    const merged = parseImeiLines(prev + '\n' + collected.join('\n'));
                    return merged.join('\n');
                });
                setResult({ severity: 'info', text: `Loaded ${collected.length} IMEI(s) from Excel — review and submit.` });
            } catch (err) {
                setResult({ severity: 'error', text: `Excel parse error: ${err.message}` });
            } finally {
                if (fileInputRef.current) fileInputRef.current.value = '';
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const handleSubmit = async () => {
        const imeis = parseImeiLines(text);
        if (imeis.length === 0) {
            setResult({ severity: 'warning', text: 'No IMEIs to process.' });
            return;
        }
        setBusy(true);
        setResult(null);
        setProgress({ done: 0, total: imeis.length });

        const existing = new Set((ImeiProduct || []).map((x) => x?.stockcontrollers?.imei).filter(Boolean));
        const duplicates = imeis.filter((i) => existing.has(i));
        const toLookup = imeis.filter((i) => !existing.has(i));
        const errors = duplicates.map((imei) => ({ imei, reason: 'duplicate (already added)' }));
        let newItems = [];

        if (toLookup.length === 0) {
            setBusy(false);
            setResult({
                severity: errors.length === 0 ? 'success' : 'warning',
                text: `Added 0/${imeis.length}. Errors: ${errors.length}.`,
                errors,
            });
            return;
        }

        try {
            const res = await axios.post(
                ApiLink + '/api/stockcontroller/stock/imei/bulk',
                {
                    imeis: toLookup,
                    productIds: Array.from(allowedProductIds),
                },
                { withCredentials: true, headers: { token: localStorage.getItem('token') } },
            );
            newItems = Array.isArray(res?.data?.data) ? res.data.data : [];
            const serverUnmatched = Array.isArray(res?.data?.unmatched) ? res.data.unmatched : [];
            serverUnmatched.forEach((u) => errors.push(u));
            setProgress({ done: imeis.length, total: imeis.length });
        } catch (err) {
            setBusy(false);
            setResult({
                severity: 'error',
                text: err?.response?.data?.msg || err.message,
                errors,
            });
            return;
        }

        // Hard-block: if every IMEI errored, stay open so the user can see why.
        if (newItems.length === 0) {
            setBusy(false);
            setResult({
                severity: 'error',
                text: `Added 0/${imeis.length}. Errors: ${errors.length}.`,
                errors,
            });
            return;
        }

        // Happy path: stash the items, auto-close the dialog. The actual
        // setImeiProduct call happens in onExited (see comment at top).
        pendingRef.current = {
            newItems,
            errors,
            requested: imeis.length,
        };
        setBusy(false);
        setOpen(false);
    };

    return (
        <>
            <Button
                variant="outlined"
                color="secondary"
                startIcon={<PlaylistAddIcon />}
                onClick={() => setOpen(true)}
                sx={{ marginTop: 5, marginLeft: 2 }}
            >
                Multi IMEI / Excel Import
            </Button>

            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                TransitionProps={{ onExited: handleExited }}
            >
                <DialogTitle>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <PlaylistAddIcon color="primary" />
                        <Typography variant="h4">Bulk Import IMEIs</Typography>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={1.5}>
                        <Typography variant="body2" color="textSecondary">
                            Paste IMEIs (one per line, comma or space separated) or upload an Excel file with an
                            <strong> imei </strong> column.
                        </Typography>
                        <Stack direction="row" spacing={1}>
                            <Button
                                variant="outlined"
                                startIcon={<UploadFileIcon />}
                                onClick={() => fileInputRef.current?.click()}
                                disabled={busy}
                            >
                                Upload Excel (.xlsx)
                            </Button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".xlsx,.xls,.csv"
                                hidden
                                onChange={handleExcelPick}
                            />
                            <Button
                                variant="text"
                                color="error"
                                onClick={() => setText('')}
                                disabled={busy || !text}
                            >
                                Clear
                            </Button>
                        </Stack>
                        <TextField
                            multiline
                            minRows={6}
                            maxRows={14}
                            placeholder={'352262371617556\n352262371618786\n...'}
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            disabled={busy}
                            fullWidth
                            inputProps={{ style: { fontFamily: 'monospace' } }}
                        />
                        <Typography variant="caption" color="textSecondary">
                            {parseImeiLines(text).length} IMEI(s) ready
                        </Typography>
                        {busy && (
                            <Stack spacing={0.5}>
                                <LinearProgress />
                                <Typography variant="caption" color="textSecondary">
                                    Validating {progress.total} IMEI(s) on server…
                                </Typography>
                            </Stack>
                        )}
                        {result && (
                            <Alert severity={result.severity} icon={result.severity === 'success' ? <CheckCircleIcon /> : <ErrorOutlineIcon />}>
                                {result.text}
                            </Alert>
                        )}
                        {result?.errors?.length > 0 && (
                            <Box sx={{ border: '1px solid #eee', borderRadius: 1 }}>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                    sx={{ px: 1, py: 0.5, bgcolor: '#fafafa', borderBottom: '1px solid #eee' }}
                                >
                                    <Typography variant="caption" sx={{ flex: 1, color: '#c62828', fontWeight: 600 }}>
                                        Errors ({result.errors.length})
                                    </Typography>
                                    <Button
                                        size="small"
                                        startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                                        onClick={() => {
                                            const txt = result.errors
                                                .map((e) => `${e.imei}\t${e.reason}`)
                                                .join('\n');
                                            navigator.clipboard?.writeText(txt);
                                        }}
                                    >
                                        Copy
                                    </Button>
                                    <Button
                                        size="small"
                                        onClick={() => {
                                            const list = result.errors.map((e) => e.imei).join('\n');
                                            setText(list);
                                        }}
                                    >
                                        Reload to textarea
                                    </Button>
                                </Stack>
                                <Box
                                    sx={{
                                        maxHeight: 320,
                                        overflowY: 'auto',
                                        p: 1,
                                        fontFamily: 'monospace',
                                        fontSize: 12,
                                    }}
                                >
                                    {result.errors.map((e, i) => (
                                        <div key={i}>
                                            <span style={{ color: '#999', display: 'inline-block', minWidth: 28 }}>
                                                {i + 1}.
                                            </span>
                                            <span style={{ color: '#c62828' }}>{e.imei}</span> — {e.reason}
                                        </div>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </Stack>
                </DialogContent>
                <Divider />
                <DialogActions>
                    <Button onClick={handleClose} disabled={busy} color="secondary">
                        Close
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={busy || parseImeiLines(text).length === 0}
                        variant="contained"
                        color="primary"
                        startIcon={<PlaylistAddIcon />}
                    >
                        {busy ? 'Processing…' : 'Submit IMEIs'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!toast}
                autoHideDuration={3500}
                onClose={() => setToast(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                {toast ? (
                    <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled">
                        {toast.text}
                    </Alert>
                ) : undefined}
            </Snackbar>
        </>
    );
}
